import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { registerDestructor } from '@ember/destroyable';
import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { or, eq, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import set from 'ember-set-helper/helpers/set';
import {
  HdsModal,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import { Loading } from 'core/components';
import { boxMargin } from 'core/modifiers';
import { GEOS } from 'core/utils/constants';
import AlertBanner from 'hcp/components/page/account-settings/security/alert-banner';
import MfaChallenge from 'hcp/components/manage-mfa/enable/mfa-challenge';
import RecoveryCode from 'hcp/components/manage-mfa/enable/recovery-code';
import Footer from 'hcp/components/manage-mfa/enable/footer';

const STATES = {
  GET_TOKEN: 'get_token',
  LOADING: 'loading',
  MFA_CHALLENGE: 'mfa_challenge',
  RECOVERY_CODE: 'recovery_code',
};

export default class ManageMfaEnable extends Component {
  @service api;
  @service config;
  @service intl;
  @service mfa;
  @service flashMessages;

  @tracked mfaToken;
  @tracked enableMFAResponse;

  // set from child components
  @tracked mfaIsConfirmed = false;
  @tracked downloadedRecoveryCode = false;

  REGION_US = GEOS.US;
  STATES = STATES;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      this.mfaToken = '';
      this.enableMFAResponse = '';
      this.mfaIsConfirmed = false;
      this.downloadedRecoveryCode = false;
    });
  }

  get state() {
    if (this.popupForMFAToken.isRunning) {
      return STATES.LOADING;
    } else if (!this.mfaToken) {
      return STATES.GET_TOKEN;
    } else if (!this.mfaIsConfirmed) {
      return STATES.MFA_CHALLENGE;
    } else {
      return STATES.RECOVERY_CODE;
    }
  }

  isState = (state) => {
    return this.state === state;
  };

  get footerButtonText() {
    let i18nKeys = {
      [STATES.GET_TOKEN]:
        'components.page.account-settings.security.mfaModal.step1.continueBtn',
      [STATES.MFA_CHALLENGE]:
        'components.page.account-settings.security.mfaModal.step2.verifyBtn',
      [STATES.RECOVERY_CODE]:
        'components.page.account-settings.security.mfaModal.step3.finishBtn',
    };
    return this.intl.t(i18nKeys[this.state]);
  }

  get deployGeo() {
    return this.config?.app?.deployGeo ?? GEOS.US;
  }

  popupForMFAToken = task(async (evt) => {
    evt.preventDefault();
    try {
      const mfaToken = await this.mfa.getMFAToken(this.args.onCancel);
      const enableMFAResponse = await this.api.profile.profileServiceEnableMFA({
        mfaToken,
      });
      this.enableMFAResponse = enableMFAResponse;
      this.mfaToken = mfaToken;
    } catch {
      this.flashMessages.error(
        this.intl.t('components.page.account-settings.security.mfa.tokenError')
      );
      this.args.onCancel();
    }
  });

  handleMFASuccess = () => {
    this.flashMessages.success(
      this.intl.t(
        'components.page.account-settings.security.mfaEnable.flashMessage.success'
      )
    );
    this.args.onStatusChange();
    this.args.onCancel();
  };

  handleMFAError = (errorMsg) => {
    let error =
      errorMsg ??
      'components.page.account-settings.security.mfaEnable.flashMessage.error';
    this.flashMessages.error(this.intl.t(error));
    this.args.onCancel();
  };
  <template>
    <HdsModal
      @onClose={{@onCancel}}
      @isDismissDisabled={{or
        (this.isState this.STATES.LOADING)
        (this.isState this.STATES.RECOVERY_CODE)
      }}
      as |M|
    >
      <M.Header @icon="alert-circle" data-test-mfa-modalHeader>
        {{t "components.page.account-settings.security.mfaModal.step1.heading"}}
      </M.Header>
      <M.Body ...attributes>
        {{#if (this.isState this.STATES.LOADING)}}
          <Loading data-test-mfa-loading as |L|>
            <L.Message>
              {{t "components.page.account-settings.security.waitForPopup"}}
            </L.Message>
          </Loading>
        {{/if}}
        {{#if (this.isState this.STATES.GET_TOKEN)}}
          {{#if (eq this.deployGeo this.REGION_US)}}
            <AlertBanner
              data-test-mfa-disabled-warning
              @header={{t
                "components.page.account-settings.security.banner.heading.mfa"
              }}
              {{boxMargin "0 0 md 0"}}
            />
          {{/if}}
          <form
            id="{{this.state}}-form"
            {{on "submit" this.popupForMFAToken.perform}}
          >
            <HdsTextBody @tag="p">
              {{t
                "components.page.account-settings.security.mfaModal.step1.body"
              }}
            </HdsTextBody>
          </form>
        {{/if}}
        {{#if (this.isState this.STATES.MFA_CHALLENGE)}}
          <MfaChallenge
            @onError={{this.handleMFAError}}
            @onConfirm={{set this "mfaIsConfirmed" true}}
            @mfaToken={{this.mfaToken}}
            @qrCodeUri={{this.enableMFAResponse.qrcodeUri}}
            @secret={{this.enableMFAResponse.secret}}
            @formId="{{this.state}}-form"
          />
        {{/if}}
        {{#if (this.isState this.STATES.RECOVERY_CODE)}}
          <RecoveryCode
            @mfaRecoveryCode={{get this.enableMFAResponse.recoveryCodes 0}}
            @handleMFASuccess={{this.handleMFASuccess}}
            @formId="{{this.state}}-form"
            @downloadedRecoveryCode={{this.downloadedRecoveryCode}}
            @onCheck={{set
              this
              "downloadedRecoveryCode"
              (not this.downloadedRecoveryCode)
            }}
          />
        {{/if}}
      </M.Body>
      {{#unless (this.isState this.STATES.LOADING)}}
        <M.Footer as |F|>
          <Footer
            @formId="{{this.state}}-form"
            @close={{F.close}}
            @hideCancel={{this.isState this.STATES.RECOVERY_CODE}}
            @buttonText={{this.footerButtonText}}
          />
        </M.Footer>
      {{/unless}}
    </HdsModal>
  </template>
}
